import React, { ButtonHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'text' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  $variant?: ButtonVariant;
  $fullWidth?: boolean;
  $size?: 'small' | 'medium' | 'large';
}

const getButtonStyles = (variant: ButtonVariant = 'primary') => {
  switch (variant) {
    case 'primary':
      return css`
        background-color: ${({ theme }) => theme.colors.primary};
        color: white;
        border: none;
        
        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.primaryDark};
        }
        
        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.primaryDarker};
        }
      `;
    case 'secondary':
      return css`
        background-color: ${({ theme }) => theme.colors.secondary};
        color: white;
        border: none;
        
        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.secondaryDark};
        }
        
        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.secondaryDarker};
        }
      `;
    case 'outline':
      return css`
        background-color: transparent;
        color: ${({ theme }) => theme.colors.primary};
        border: 1px solid ${({ theme }) => theme.colors.primary};
        
        &:hover:not(:disabled) {
          background-color: ${({ theme }) => `${theme.colors.primary}10`};
        }
        
        &:active:not(:disabled) {
          background-color: ${({ theme }) => `${theme.colors.primary}20`};
        }
      `;
    case 'text':
      return css`
        background-color: transparent;
        color: ${({ theme }) => theme.colors.primary};
        border: none;
        padding: 8px 12px;
        
        &:hover:not(:disabled) {
          background-color: ${({ theme }) => `${theme.colors.primary}10`};
        }
        
        &:active:not(:disabled) {
          background-color: ${({ theme }) => `${theme.colors.primary}20`};
        }
      `;
    case 'danger':
      return css`
        background-color: ${({ theme }) => theme.colors.error};
        color: white;
        border: none;
        
        &:hover:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.errorDark};
        }
        
        &:active:not(:disabled) {
          background-color: ${({ theme }) => theme.colors.errorDarker};
        }
      `;
    default:
      return css``;
  }
};

const getSizeStyles = (size: 'small' | 'medium' | 'large' = 'medium') => {
  switch (size) {
    case 'small':
      return css`
        padding: 6px 12px;
        font-size: 12px;
      `;
    case 'medium':
      return css`
        padding: 10px 16px;
        font-size: 14px;
      `;
    case 'large':
      return css`
        padding: 12px 20px;
        font-size: 16px;
      `;
    default:
      return css``;
  }
};

const StyledButton = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  
  ${({ $variant }) => getButtonStyles($variant)};
  ${({ $size }) => getSizeStyles($size)};
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${({ theme, $variant }) => 
      $variant === 'danger' 
        ? `${theme.colors.error}40` 
        : `${theme.colors.primary}40`};
  }
`;

const Button: React.FC<ButtonProps> = ({ 
  children, 
  $variant = 'primary',
  $size = 'medium',
  $fullWidth = false,
  ...props 
}) => {
  return (
    <StyledButton 
      $variant={$variant} 
      $size={$size} 
      $fullWidth={$fullWidth} 
      {...props}
    >
      {children}
    </StyledButton>
  );
};

export default Button;

